import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getPGPropertiesDropDown,
  getSocietyAmenitiesList,
  getPGRooms,
  getPgRoomsById,
  createPgRooms,
  updatePgRooms,
  toggleStatusPgRooms,
  removePgRooms,
  getManageBedList,
  getManageBedSharingType,
  saveManageBeds,
  deleteManageBed,
  toggleManageBed,
} from "../../../services/propertyOwner/propertyOwnerServices";

const INITIAL_ADD_FORM = {
  pgPropertyId: "",
  roomName: "",
  floor: 1,
  totalBeds: 1,
  amenityIds: [],
  photos: [],
  video: null,
};

export default function PgRoomAndBed() {
  const [roomData, setRoomData] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [dropdown, setDropdown] = useState([]);
  const [filterPgId, setFilterPgId] = useState("");
  const [amenitiesList, setAmenitiesList] = useState([]);

  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);

  const [addForm, setAddForm] = useState(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);
  const [addPhotoPreviews, setAddPhotoPreviews] = useState([]);

  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editPhotoPreviews, setEditPhotoPreviews] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    roomId: null,
  });

  const [manageBedModal, setManageBedModal] = useState({
    open: false,
    room: null,
  });
  const [manageBedData, setManageBedData] = useState(null);
  const [manageBedLoading, setManageBedLoading] = useState(false);
  const [sharingTypes, setSharingTypes] = useState([]);

  const [bedList, setBedList] = useState([]); // controlled list for editing
  const [saveBedLoading, setSaveBedLoading] = useState(false);
  const [bedTogglingId, setBedTogglingId] = useState(null);
  const [bedDeletingId, setBedDeletingId] = useState(null);

  useEffect(() => {
    fetchDropdown();
    fetchRooms();
  }, []);

  useEffect(() => {
    fetchRooms(filterPgId);
  }, [filterPgId]);

  const fetchDropdown = async () => {
    try {
      const res = await getPGPropertiesDropDown();
      if (res.statusCode === 1) setDropdown(res.data ?? []);
    } catch (err) {
      console.log("Failed to fetch dropdown");
    }
  };

  const fetchRooms = async (pgPropertyId = "") => {
    setListLoading(true);
    try {
      const res = await getPGRooms(pgPropertyId || undefined);
      if (res.statusCode === 1) setRoomData(res.data);
    } catch (err) {
      console.log(err.response?.data?.message || "Failed to fetch rooms");
    } finally {
      setListLoading(false);
    }
  };

  const fetchAmenities = async () => {
    try {
      const res = await getSocietyAmenitiesList();
      if (res.statusCode === 1) setAmenitiesList(res.data.amenities || []);
    } catch (err) {
      console.log("Failed to fetch amenities");
    }
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  const openAddModal = () => {
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setAddPhotoPreviews([]);
    fetchAmenities();
    setIsOpenAddModal(true);
  };

  const closeAddModal = () => {
    setIsOpenAddModal(false);
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setAddPhotoPreviews([]);
  };

  const handleAddPhotos = (e) => {
    const files = Array.from(e.target.files);
    setAddForm((p) => ({ ...p, photos: files }));
    setAddPhotoPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const handleAddVideo = (e) => {
    setAddForm((p) => ({ ...p, video: e.target.files[0] || null }));
  };

  const handleAddAmenityToggle = (id) => {
    setAddForm((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(id)
        ? prev.amenityIds.filter((a) => a !== id)
        : [...prev.amenityIds, id],
    }));
  };

  const validateAdd = () => {
    const e = {};
    if (!addForm.pgPropertyId) e.pgPropertyId = "PG Property is required.";
    if (!addForm.roomName) e.roomName = "Room name is required.";
    if (!addForm.totalBeds) e.totalBeds = "Total beds is required.";
    setAddErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddSubmit = async () => {
    if (!validateAdd()) return;
    setAddLoading(true);
    try {
      const fd = new FormData();
      fd.append("PGPropertyId", addForm.pgPropertyId);
      fd.append("RoomName", addForm.roomName);
      fd.append("Floor", addForm.floor);
      fd.append("TotalBeds", addForm.totalBeds);
      addForm.amenityIds.forEach((id) => fd.append("amenityIds", id));
      addForm.photos.forEach((f) => fd.append("photos", f));
      if (addForm.video) fd.append("video", addForm.video);

      const res = await createPgRooms(fd);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddModal();
        fetchRooms(filterPgId);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message);
    } finally {
      setAddLoading(false);
    }
  };

  const openEditModal = async (id) => {
    try {
      const res = await getPgRoomsById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedRoom(d);
        setEditForm({
          pgPropertyId: d.pgPropertyId || "",
          roomName: d.roomName || "",
          floor: d.floor || 1,
          totalBeds: d.totalBeds || 1,
          amenityIds: d.amenityIds
            ? d.amenityIds.split(",").map(Number).filter(Boolean)
            : [],
          photos: [],
          video: null,
        });
        setExistingPhotos(d.photosJson ? JSON.parse(d.photosJson) : []);
        setEditPhotoPreviews([]);
        setEditErrors({});
        fetchAmenities();
        setIsOpenEditModal(true);
      }
    } catch (err) {
      toast.error("Failed to load room details.");
    }
  };

  const closeEditModal = () => {
    setIsOpenEditModal(false);
    setSelectedRoom(null);
    setEditForm({});
    setEditErrors({});
    setEditPhotoPreviews([]);
    setExistingPhotos([]);
  };

  const handleEditPhotos = (e) => {
    const files = Array.from(e.target.files);
    setEditForm((p) => ({ ...p, photos: files }));
    setEditPhotoPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const handleEditVideo = (e) => {
    setEditForm((p) => ({ ...p, video: e.target.files[0] || null }));
  };

  const handleEditAmenityToggle = (id) => {
    setEditForm((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(id)
        ? prev.amenityIds.filter((a) => a !== id)
        : [...prev.amenityIds, id],
    }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.roomName) e.roomName = "Room name is required.";
    if (!editForm.totalBeds) e.totalBeds = "Total beds is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const fd = new FormData();
      fd.append("PGPropertyId", editForm.pgPropertyId);
      fd.append("RoomName", editForm.roomName);
      fd.append("Floor", editForm.floor);
      fd.append("TotalBeds", editForm.totalBeds);
      editForm.amenityIds.forEach((id) => fd.append("amenityIds", id));
      editForm.photos.forEach((f) => fd.append("photos", f));
      if (editForm.video) fd.append("video", editForm.video);

      const res = await updatePgRooms(selectedRoom.pgRoomId, fd);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditModal();
        fetchRooms(filterPgId);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await toggleStatusPgRooms(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchRooms(filterPgId);
      }
    } catch (err) {
      toast.error("Failed to toggle status.");
    }
  };

  const openDeleteModal = (r) => {
    setDeleteModal({
      open: true,
      roomId: r.pgRoomId,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ open: false, roomId: null });
  };

  const handleDeleteRoom = async () => {
    try {
      const res = await removePgRooms(deleteModal.roomId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeDeleteModal();
        fetchRooms();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete Room.");
    }
  };

  const openManageBedModal = async (r) => {
    setManageBedModal({ open: true, room: r });
    setManageBedData(null);
    setManageBedLoading(true);
    try {
      const [bedsRes, sharingRes] = await Promise.all([
        getManageBedList(r.pgRoomId),
        getManageBedSharingType(),
      ]);
      if (bedsRes.statusCode === 1) {
        setManageBedData(bedsRes.data);
        setBedList(
          bedsRes.data.beds.map((b) => ({
            bedId: b.bedId,
            bedNumber: b.bedNumber,
            sharingTypeId: b.sharingTypeId,
            status: b.status,
            isActive: b.isActive,
            isNew: false,
          })),
        );
      }
      if (sharingRes.statusCode === 1) setSharingTypes(sharingRes.data || []);
    } catch {
      console.log("Failed to load beds");
    } finally {
      setManageBedLoading(false);
    }
  };

  const closeManageBedModal = () => {
    setManageBedModal({ open: false, room: null });
    setManageBedData(null);
    setBedList([]);
  };

  const handleAddBed = () => {
    setBedList((prev) => [
      ...prev,
      {
        bedId: null,
        bedNumber: "",
        sharingTypeId: sharingTypes[0]?.sharingTypeId || "",
        status: "Vacant",
        isActive: true,
        isNew: true,
      },
    ]);
  };

  const handleBedChange = (idx, field, value) => {
    setBedList((prev) =>
      prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)),
    );
  };

  const handleRemoveNewBed = (idx) => {
    setBedList((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleToggleBed = async (bedId) => {
    setBedTogglingId(bedId);
    try {
      const res = await toggleManageBed(bedId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setBedList((prev) =>
          prev.map((b) =>
            b.bedId === bedId ? { ...b, isActive: !b.isActive } : b,
          ),
        );
      } else toast.error(res.message);
    } catch {
      console.log("Failed to toggle bed");
    } finally {
      setBedTogglingId(null);
    }
  };

  const handleDeleteBed = async (bedId) => {
    setBedDeletingId(bedId);
    try {
      const res = await deleteManageBed(bedId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setBedList((prev) => prev.filter((b) => b.bedId !== bedId));
        setManageBedData((prev) =>
          prev ? { ...prev, totalBeds: prev.totalBeds - 1 } : prev,
        );
      } else toast.error(res.message);
    } catch {
      toast.error("Failed to delete bed");
    } finally {
      setBedDeletingId(null);
    }
  };

const handleSaveBeds = async () => {
  const newBeds = bedList.filter((b) => b.isNew);
  const existingBeds = bedList.filter((b) => !b.isNew);

  if (newBeds.length === 0 && existingBeds.length === 0) {
    toast.error("No beds to save.");
    return;
  }

  setSaveBedLoading(true);
  try {
    const calls = [];

    // New beds — pgRoomId pass karo, bedId nahi
    if (newBeds.length > 0) {
      calls.push(
        saveManageBeds({
          pgRoomId: manageBedModal.room?.pgRoomId,
          beds: newBeds.map((b) => ({
            bedNumber: Number(b.bedNumber),
            sharingTypeId: Number(b.sharingTypeId),
          })),
        })
      );
    }

    // Existing beds — har bed ka bedId bhi pass karo
    if (existingBeds.length > 0) {
      calls.push(
        saveManageBeds({
          pgRoomId: manageBedModal.room?.pgRoomId,
          beds: existingBeds.map((b) => ({
            bedId: b.bedId,
            bedNumber: Number(b.bedNumber),
            sharingTypeId: Number(b.sharingTypeId),
          })),
        })
      );
    }

    const results = await Promise.all(calls);
    const allOk = results.every((r) => r.statusCode === 1);

    if (allOk) {
      // toast.success("Beds saved successfully.");
        toast.success(results[results.length - 1].message);
      // Reload list
      const listRes = await getManageBedList(manageBedModal.room?.pgRoomId);
      if (listRes.statusCode === 1) {
        setManageBedData(listRes.data);
        setBedList(listRes.data.beds.map((b) => ({ ...b, isNew: false })));
      }
    } else {
      toast.error("Some beds could not be saved.");
    }
  } catch {
    toast.error("Failed to save beds.");
  } finally {
    setSaveBedLoading(false);
  }
};

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              Rooms &amp; Beds
            </div>
            <div className="page-sub" id="page-sub">
              Manage PG rooms and bed allocation
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Room
            </button>
          </div>
        </div>

        <div className="content" id="content-area">
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">🚪</div>
              <div className="stat-label">Total Rooms</div>
              <div className="stat-value">{roomData?.statTotalRooms ?? 0}</div>
              <div className="stat-delta delta-neu">Across all PGs</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🛏</div>
              <div className="stat-label">Total Beds</div>
              <div className="stat-value">{roomData?.statTotalBeds ?? 0}</div>
              <div className="stat-delta delta-neu">Capacity</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Occupied</div>
              <div className="stat-value">{roomData?.statOccupied ?? 0}</div>
              <div className="stat-delta delta-up">Active tenants</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🔓</div>
              <div className="stat-label">Vacant</div>
              <div className="stat-value">{roomData?.statVacant ?? 0}</div>
              <div className="stat-delta delta-dn">Available beds</div>
            </div>
          </div>

          <div className="card">
            <div className="flex-between mb12">
              <div className="card-title" style={{ margin: 0 }}>
                All Rooms &amp; Beds
              </div>
              <select
                style={{ fontSize: 12, padding: "5px 8px", width: "auto" }}
                value={filterPgId}
                onChange={(e) => setFilterPgId(e.target.value)}
              >
                <option value="">All PG Properties</option>
                {dropdown.map((d) => (
                  <option key={d.pgPropertyId} value={d.pgPropertyId}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Room</th>
                    <th>PG Property</th>
                    <th>Beds</th>
                    <th>Bed Status</th>
                    <th>Occupancy</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: j === 1 ? "80%" : "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : roomData?.rooms?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No rooms found.
                      </td>
                    </tr>
                  ) : (
                    roomData?.rooms?.map((r, idx) => (
                      <tr key={r.pgRoomId}>
                        <td className="sn-col">{idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{r.roomName}</div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-tertiary)",
                            }}
                          >
                            Floor {r.floor}
                          </div>
                        </td>
                        <td style={{ fontSize: 11 }}>{r.pgPropertyName}</td>
                        <td style={{ fontSize: 12, textAlign: "center" }}>
                          <strong>{r.totalBeds}</strong>
                        </td>
                        <td>
                          {Array.from({ length: r.totalBeds }).map((_, i) => {
                            const isOccupied = i < r.occupiedBeds;
                            return (
                              <span
                                key={i}
                                title={`Bed ${i + 1}: ${isOccupied ? "Occupied" : "Vacant"}`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: 28,
                                  height: 28,
                                  borderRadius: "50%",
                                  background: isOccupied
                                    ? "var(--green)20"
                                    : "var(--text-tertiary)20",
                                  border: `1.5px solid ${isOccupied ? "var(--green)" : "var(--text-tertiary)"}`,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  color: isOccupied
                                    ? "var(--green)"
                                    : "var(--text-tertiary)",
                                  cursor: "default",
                                  marginRight: 3,
                                }}
                              >
                                {i + 1}
                              </span>
                            );
                          })}
                        </td>
                        <td>
                          <span
                            style={{
                              color: "var(--green)",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {r.occupiedBeds} Occupied
                          </span>
                          {" / "}
                          <span
                            style={{
                              color: "var(--amber)",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            {r.vacantBeds} Vacant
                          </span>
                        </td>
                        <td>
                          <span
                            className={`badge ${r.isActive ? "badge-active" : "badge-inactive"}`}
                          >
                            {r.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              onClick={() => openEditModal(r.pgRoomId)}
                            >
                              <span className="tip">Edit</span>✎
                            </span>
                            {r.isActive ? (
                              <span
                                className="action-icon red"
                                onClick={() => handleToggleStatus(r.pgRoomId)}
                              >
                                <span className="tip">Deactivate</span>⊗
                              </span>
                            ) : (
                              <span
                                className="action-icon green"
                                onClick={() => handleToggleStatus(r.pgRoomId)}
                              >
                                <span className="tip">Activate</span>✓
                              </span>
                            )}
                            <span
                              className="action-icon"
                              onClick={() => openDeleteModal(r)}
                            >
                              <span className="tip">Delete</span>🗑
                            </span>

                            <span
                              className="action-icon"
                              onClick={() => openManageBedModal(r)}
                            >
                              <span className="tip">Manage Bed</span>🛏
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      {isOpenAddModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Add New Room</div>
              <button className="modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group" style={{ gridColumn: "1/-1" }}>
                  <label>PG Property *</label>
                  <select
                    value={addForm.pgPropertyId}
                    onChange={(e) => {
                      setAddForm((p) => ({
                        ...p,
                        pgPropertyId: e.target.value,
                      }));
                      setAddErrors((p) => ({ ...p, pgPropertyId: "" }));
                    }}
                    style={
                      addErrors.pgPropertyId ? { borderColor: "#dc2626" } : {}
                    }
                  >
                    <option value="">-- Select PG Property --</option>
                    {dropdown.map((d) => (
                      <option key={d.pgPropertyId} value={d.pgPropertyId}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                  {addErrors.pgPropertyId && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.pgPropertyId}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Room Name / Number *</label>
                  <input
                    type="text"
                    value={addForm.roomName}
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, roomName: e.target.value }));
                      setAddErrors((p) => ({ ...p, roomName: "" }));
                    }}
                    placeholder="e.g. Room 101"
                    style={addErrors.roomName ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.roomName && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.roomName}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Floor</label>
                  <input
                    type="number"
                    value={addForm.floor}
                    min={0}
                    onChange={(e) =>
                      setAddForm((p) => ({
                        ...p,
                        floor: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Total Beds (Capacity) *</label>
                  <input
                    type="number"
                    value={addForm.totalBeds}
                    min={1}
                    max={10}
                    onChange={(e) => {
                      setAddForm((p) => ({
                        ...p,
                        totalBeds: Number(e.target.value),
                      }));
                      setAddErrors((p) => ({ ...p, totalBeds: "" }));
                    }}
                    style={
                      addErrors.totalBeds ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {addErrors.totalBeds && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.totalBeds}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group mb14">
                <label>Amenities</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {amenitiesList.map((a) => (
                    <label
                      key={a.amenityId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "5px 8px",
                        borderRadius: 6,
                        background: addForm.amenityIds.includes(a.amenityId)
                          ? "var(--blue-light)"
                          : "transparent",
                        border: `0.5px solid ${addForm.amenityIds.includes(a.amenityId) ? "var(--blue)" : "var(--border-light)"}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addForm.amenityIds.includes(a.amenityId)}
                        onChange={() => handleAddAmenityToggle(a.amenityId)}
                      />
                      <span>{a.icon}</span> {a.amenityName}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  📷 Room Photos{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Multiple)
                  </span>
                </label>
                {addPhotoPreviews.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {addPhotoPreviews.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt=""
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "0.5px solid var(--blue)",
                        }}
                      />
                    ))}
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--blue-light)",
                    color: "var(--blue)",
                    border: "1px solid var(--blue)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  📁 Choose Photos
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleAddPhotos}
                  />
                </label>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  🎥 Room Video{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Optional)
                  </span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--purple-light)",
                    color: "var(--purple)",
                    border: "1px solid var(--purple)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  🎬 Choose Video
                  <input
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={handleAddVideo}
                  />
                </label>
                {addForm.video && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    {addForm.video.name}
                  </span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModal}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "💾 Save Room"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {isOpenEditModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">
                Edit Room — {selectedRoom?.roomName}
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group" style={{ gridColumn: "1/-1" }}>
                  <label>PG Property</label>
                  <select
                    value={editForm.pgPropertyId}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        pgPropertyId: e.target.value,
                      }))
                    }
                  >
                    <option value="">-- Select PG Property --</option>
                    {dropdown.map((d) => (
                      <option key={d.pgPropertyId} value={d.pgPropertyId}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Room Name / Number *</label>
                  <input
                    type="text"
                    value={editForm.roomName}
                    onChange={(e) => {
                      setEditForm((p) => ({ ...p, roomName: e.target.value }));
                      setEditErrors((p) => ({ ...p, roomName: "" }));
                    }}
                    placeholder="e.g. Room 101"
                    style={
                      editErrors.roomName ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {editErrors.roomName && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.roomName}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Floor</label>
                  <input
                    type="number"
                    value={editForm.floor}
                    min={0}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        floor: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Total Beds (Capacity) *</label>
                  <input
                    type="number"
                    value={editForm.totalBeds}
                    min={1}
                    max={10}
                    onChange={(e) => {
                      setEditForm((p) => ({
                        ...p,
                        totalBeds: Number(e.target.value),
                      }));
                      setEditErrors((p) => ({ ...p, totalBeds: "" }));
                    }}
                    style={
                      editErrors.totalBeds ? { borderColor: "#dc2626" } : {}
                    }
                  />
                  {editErrors.totalBeds && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.totalBeds}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group mb14">
                <label>Amenities</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3,1fr)",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {amenitiesList.map((a) => (
                    <label
                      key={a.amenityId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "5px 8px",
                        borderRadius: 6,
                        background: editForm.amenityIds?.includes(a.amenityId)
                          ? "var(--blue-light)"
                          : "transparent",
                        border: `0.5px solid ${editForm.amenityIds?.includes(a.amenityId) ? "var(--blue)" : "var(--border-light)"}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          editForm.amenityIds?.includes(a.amenityId) || false
                        }
                        onChange={() => handleEditAmenityToggle(a.amenityId)}
                      />
                      <span>{a.icon}</span> {a.amenityName}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  📷 Room Photos{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Multiple)
                  </span>
                </label>
                {existingPhotos.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {existingPhotos.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt=""
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "0.5px solid var(--border-mid)",
                        }}
                      />
                    ))}
                  </div>
                )}
                {editPhotoPreviews.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {editPhotoPreviews.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt=""
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "0.5px solid var(--blue)",
                        }}
                      />
                    ))}
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--blue-light)",
                    color: "var(--blue)",
                    border: "1px solid var(--blue)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  📁 Choose Photos
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleEditPhotos}
                  />
                </label>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  🎥 Room Video{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Optional)
                  </span>
                </label>
                {selectedRoom?.videoUrl && !editForm.video && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginBottom: 6,
                    }}
                  >
                    Current:{" "}
                    <a
                      href={selectedRoom.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "var(--blue)" }}
                    >
                      View Video
                    </a>
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--purple-light)",
                    color: "var(--purple)",
                    border: "1px solid var(--purple)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  🎬 Choose Video
                  <input
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={handleEditVideo}
                  />
                </label>
                {editForm.video && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    {editForm.video.name}
                  </span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "💾 Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal.open && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Delete Room & Bed</div>
              <button className="modal-close" onClick={closeDeleteModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ textAlign: "center", padding: "4px 0 8px" }}>
                <div style={{ fontSize: 20, marginBottom: 6 }}>🗑</div>

                <div
                  style={{
                    fontSize: 13,
                    color: "var(--text-secondary)",
                    lineHeight: 1.6,
                  }}
                >
                  This action is permanent and cannot be undone.
                </div>
              </div>
            </div>
            <div
              className="modal-footer"
              style={{ justifyContent: "center", gap: 12 }}
            >
              <button
                className="btn"
                onClick={closeDeleteModal}
                style={{ minWidth: 100 }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteRoom}
                style={{
                  minWidth: 100,
                  background: "#dc2626",
                  color: "#fff",
                  border: "none",
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Beds Modal ── */}
      {manageBedModal.open && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 680, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  🛏 Manage Beds — {manageBedModal.room?.roomName}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    marginTop: 4,
                    fontSize: 11,
                    color: "var(--text-secondary)",
                  }}
                >
                  <span>🏠 {manageBedModal.room?.pgPropertyName}</span>
                  <span>🏢 Floor {manageBedModal.room?.floor}</span>
                  <span>
                    🛏 Capacity: {manageBedModal.room?.totalBeds} beds
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={closeManageBedModal}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Stats */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 10,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    background: "var(--blue-light)",
                    borderRadius: "var(--radius-md)",
                    padding: "14px 16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "var(--blue)",
                    }}
                  >
                    {manageBedData?.totalBeds ?? manageBedModal.room?.totalBeds}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Total Beds
                  </div>
                </div>
                <div
                  style={{
                    background: "#f0fdf4",
                    borderRadius: "var(--radius-md)",
                    padding: "14px 16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "var(--green, #16a34a)",
                    }}
                  >
                    {manageBedData?.vacant ?? manageBedModal.room?.vacantBeds}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Vacant
                  </div>
                </div>
                <div
                  style={{
                    background: "#fef2f2",
                    borderRadius: "var(--radius-md)",
                    padding: "14px 16px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "var(--red, #dc2626)",
                    }}
                  >
                    {manageBedData?.occupied ??
                      manageBedModal.room?.occupiedBeds}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginTop: 2,
                    }}
                  >
                    Occupied
                  </div>
                </div>
              </div>

              {/* Bed Configuration */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                    letterSpacing: ".06em",
                  }}
                >
                  🛏 Bed Configuration
                </div>
                <button
                  className="btn btn-xs btn-primary"
                  onClick={handleAddBed}
                >
                  + Add Bed
                </button>
              </div>

              {/* {manageBedLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 60,
                      borderRadius: "var(--radius-md)",
                      background:
                        "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                      marginBottom: 8,
                    }}
                  />
                ))
              ) : manageBedData?.beds?.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    padding: 24,
                  }}
                >
                  No beds configured yet.
                </div>
              ) : (
                manageBedData?.beds?.map((bed, idx) => (
                  <div
                    key={bed.bedId}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "32px 1fr 1fr 120px 90px 32px",
                      gap: 10,
                      alignItems: "center",
                      padding: "12px 14px",
                      borderRadius: "var(--radius-md)",
                      border: `0.5px solid ${bed.status === "Occupied" ? "#fca5a5" : "var(--border-light)"}`,
                      background:
                        bed.status === "Occupied"
                          ? "#fff5f5"
                          : "var(--bg-secondary)",
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background:
                          bed.status === "Occupied"
                            ? "var(--red, #dc2626)"
                            : "var(--blue)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {idx + 1}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Bed Number
                      </div>
                      <input
                        type="number"
                        defaultValue={bed.bedNumber}
                        style={{
                          fontSize: 12,
                          padding: "5px 8px",
                          width: "80px",
                        }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Sharing Type
                      </div>
                      <select
                        defaultValue={bed.sharingTypeId}
                        style={{ fontSize: 12, padding: "5px 8px" }}
                      >
                        {sharingTypes.map((st) => (
                          <option
                            key={st.sharingTypeId}
                            value={st.sharingTypeId}
                          >
                            {st.icon} {st.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Status
                      </div>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 99,
                          background:
                            bed.status === "Occupied" ? "#fef2f2" : "#f0fdf4",
                          color:
                            bed.status === "Occupied"
                              ? "var(--red, #dc2626)"
                              : "var(--green, #16a34a)",
                          border: `0.5px solid ${bed.status === "Occupied" ? "var(--red, #dc2626)" : "var(--green, #16a34a)"}`,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background:
                              bed.status === "Occupied"
                                ? "var(--red, #dc2626)"
                                : "var(--green, #16a34a)",
                            display: "inline-block",
                          }}
                        />
                        {bed.status}
                      </span>
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Active
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <label className="toggle">
                          <input
                            type="checkbox"
                            defaultChecked={bed.isActive}
                          />
                          <span className="toggle-slider" />
                        </label>
                        <span
                          style={{
                            fontSize: 10,
                            color: bed.isActive
                              ? "var(--green, #16a34a)"
                              : "var(--text-tertiary)",
                          }}
                        >
                          {bed.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                    <button
                      disabled={bed.status === "Occupied"}
                      style={{
                        width: 28,
                        height: 28,
                        border:
                          bed.status === "Occupied"
                            ? "0.5px solid var(--border-light)"
                            : "0.5px solid #F09595",
                        background:
                          bed.status === "Occupied"
                            ? "var(--bg-secondary)"
                            : "var(--red-light, #fef2f2)",
                        color:
                          bed.status === "Occupied"
                            ? "var(--text-tertiary)"
                            : "var(--red, #dc2626)",
                        borderRadius: 5,
                        cursor:
                          bed.status === "Occupied" ? "not-allowed" : "pointer",
                        fontSize: 15,
                        fontFamily: "inherit",
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              )} */}
              {manageBedLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 60,
                      borderRadius: "var(--radius-md)",
                      background:
                        "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                      backgroundSize: "200% 100%",
                      animation: "shimmer 1.4s infinite",
                      marginBottom: 8,
                    }}
                  />
                ))
              ) : bedList.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    padding: 24,
                  }}
                >
                  No beds configured yet.
                </div>
              ) : (
                bedList.map((bed, idx) => (
                  <div
                    key={bed.bedId ?? `new-${idx}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "32px 1fr 1fr 120px 90px 32px",
                      gap: 10,
                      alignItems: "center",
                      padding: "12px 14px",
                      borderRadius: "var(--radius-md)",
                      border: `0.5px solid ${bed.isNew ? "var(--blue)" : bed.status === "Occupied" ? "#fca5a5" : "var(--border-light)"}`,
                      background: bed.isNew
                        ? "var(--blue-light)"
                        : bed.status === "Occupied"
                          ? "#fff5f5"
                          : "var(--bg-secondary)",
                      marginBottom: 8,
                    }}
                  >
                    {/* Index circle */}
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: bed.isNew
                          ? "var(--blue)"
                          : bed.status === "Occupied"
                            ? "var(--red, #dc2626)"
                            : "var(--teal)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#fff",
                      }}
                    >
                      {idx + 1}
                    </div>

                    {/* Bed Number */}
                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Bed Number
                      </div>
                      <input
                        type="number"
                        value={bed.bedNumber}
                        onChange={(e) =>
                          handleBedChange(idx, "bedNumber", e.target.value)
                        }
                        style={{
                          fontSize: 12,
                          padding: "5px 8px",
                          width: "80px",
                        }}
                      />
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Sharing Type
                      </div>
                      <select
                        value={bed.sharingTypeId}
                        onChange={(e) =>
                          handleBedChange(idx, "sharingTypeId", e.target.value)
                        }
                        style={{ fontSize: 12, padding: "5px 8px" }}
                      >
                        <option value="">— Select —</option>
                        {sharingTypes.map((st) => (
                          <option
                            key={st.sharingTypeId}
                            value={st.sharingTypeId}
                          >
                            {st.icon} {st.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Status
                      </div>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 99,
                          background:
                            bed.status === "Occupied" ? "#fef2f2" : "#f0fdf4",
                          color:
                            bed.status === "Occupied"
                              ? "var(--red, #dc2626)"
                              : "var(--green, #16a34a)",
                          border: `0.5px solid ${bed.status === "Occupied" ? "var(--red, #dc2626)" : "var(--green, #16a34a)"}`,
                        }}
                      >
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background:
                              bed.status === "Occupied"
                                ? "var(--red, #dc2626)"
                                : "var(--green, #16a34a)",
                            display: "inline-block",
                          }}
                        />
                        {bed.status}
                      </span>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "var(--text-tertiary)",
                          textTransform: "uppercase",
                          letterSpacing: ".05em",
                          marginBottom: 3,
                        }}
                      >
                        Active
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <label className="toggle">
                          <input
                            type="checkbox"
                            checked={bed.isActive}
                            disabled={bed.isNew || bedTogglingId === bed.bedId}
                            onChange={() =>
                              !bed.isNew && handleToggleBed(bed.bedId)
                            }
                          />
                          <span className="toggle-slider" />
                        </label>
                        <span
                          style={{
                            fontSize: 10,
                            color: bed.isActive
                              ? "var(--green, #16a34a)"
                              : "var(--text-tertiary)",
                          }}
                        >
                          {bedTogglingId === bed.bedId
                            ? "..."
                            : bed.isActive
                              ? "Active"
                              : "Inactive"}
                        </span>
                      </div>
                    </div>

                    {bed.isNew ? (
                      <button
                        onClick={() => handleRemoveNewBed(idx)}
                        style={{
                          width: 28,
                          height: 28,
                          border: "0.5px solid #F09595",
                          background: "var(--red-light, #fef2f2)",
                          color: "var(--red, #dc2626)",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontSize: 15,
                          fontFamily: "inherit",
                        }}
                      >
                        ×
                      </button>
                    ) : bed.status === "Occupied" ? (
                      <button
                        disabled
                        style={{
                          width: 28,
                          height: 28,
                          border: "0.5px solid var(--border-light)",
                          background: "var(--bg-secondary)",
                          color: "var(--text-tertiary)",
                          borderRadius: 5,
                          cursor: "not-allowed",
                          fontSize: 13,
                          fontFamily: "inherit",
                        }}
                      >
                        🗑
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeleteBed(bed.bedId)}
                        disabled={bedDeletingId === bed.bedId}
                        style={{
                          width: 28,
                          height: 28,
                          border: "0.5px solid #F09595",
                          background: "var(--red-light, #fef2f2)",
                          color: "var(--red, #dc2626)",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontSize: 13,
                          fontFamily: "inherit",
                        }}
                      >
                        {bedDeletingId === bed.bedId ? "..." : "🗑"}
                      </button>
                    )}
                  </div>
                ))
              )}

              <div
                style={{
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  marginTop: 8,
                }}
              >
                Room capacity: {manageBedModal.room?.totalBeds} beds · Currently
                configured: {manageBedData?.beds?.length ?? 0}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn" onClick={closeManageBedModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveBeds}
                disabled={saveBedLoading}
              >
                {saveBedLoading ? "Saving..." : "💾 Save Beds"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
